import * as React from 'react';
import { codeToTokens } from 'shiki';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Code as Interface } from '../../design/components';

export const Code: React.FC<Interface> = ({
  name, description, state, hidden, inline, readonly, disabled, required, error, multiline = 14
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [focused, setFocused] = React.useState(false);
  const [tokens, setTokens] = React.useState<{ content: string; color?: string }[][]>([]);

  React.useEffect(() => {
    const code = value || '';
    if (!code) {
      setTokens([]);
      return;
    }
    codeToTokens(code, { lang: 'javascript', theme: 'github-light' })
      .then(result => {
        setTokens(result.tokens.map(line =>
          line.map(token => ({ content: token.content, color: token.color }))
        ));
      })
      .catch(() => setTokens([]));
  }, [value]);

  // Inline mode: truncated single-line code with tooltip
  if (inline) {
    const maxChars = 40;
    const code = value || '';
    const singleLine = code.replace(/\n/g, ' ').trim();
    const truncated = singleLine.length > maxChars
      ? singleLine.slice(0, maxChars) + '...'
      : singleLine;

    return (
      <code
        className={cn(
          'font-mono text-[11px] px-1 py-0.5 rounded bg-muted whitespace-nowrap',
          hidden && 'hidden'
        )}
      >
        {tokens.length > 0 ? (
          tokens[0]?.map((token, i) => (
            <span key={i} style={{ color: token.color }}>
              {token.content.slice(0, maxChars)}
            </span>
          ))
        ) : truncated}
      </code>
    );
  }

  const actualLines = (value || '').split('\n').length;
  const lineCount = Math.min(Math.max(actualLines, 1), multiline);
  const atMax = actualLines > multiline;
  const lineHeightPx = 20;
  const maxHeight = atMax ? `${multiline * lineHeightPx + 16}px` : undefined;

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <div
        className={cn(
          'relative grid rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] outline-none',
          focused ? 'border-ring ring-ring/50 ring-[3px]' : 'border-input',
          error && 'border-destructive',
          atMax && 'overflow-auto'
        )}
        style={{ maxHeight }}
      >
        {/* Highlighted code layer (behind) */}
        <pre
          aria-hidden="true"
          className={cn(
            'font-mono text-sm leading-5 m-0 px-3 py-2',
            'whitespace-pre-wrap break-words',
            '[grid-area:1/1/2/2] overflow-hidden pointer-events-none bg-transparent'
          )}
        >
          {tokens.length > 0 ? (
            tokens.map((line, lineIdx) => (
              <React.Fragment key={lineIdx}>
                {line.map((token, tokenIdx) => (
                  <span key={tokenIdx} style={{ color: token.color }}>
                    {token.content}
                  </span>
                ))}
                {lineIdx < tokens.length - 1 && '\n'}
              </React.Fragment>
            ))
          ) : (value || '') + '\n'}
        </pre>

        {/* Transparent textarea (in front for input) */}
        <textarea
          id={name}
          name={name}
          value={value || ''}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          rows={lineCount}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => setValue(e.target.value)}
          spellCheck={false}
          placeholder={name || 'code'}
          className={cn(
            'font-mono text-sm leading-5 m-0 px-3 py-2',
            'whitespace-pre-wrap break-words',
            '[grid-area:1/1/2/2] w-full box-border resize-none',
            'border-none bg-transparent text-transparent caret-foreground outline-none',
            disabled && 'cursor-not-allowed opacity-50',
            readonly && 'cursor-default'
          )}
        />
      </div>

      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};
